// We need to keep this security util file in the ava repository since ava extends the kyoko security type
// in kyoko.d.ts

import {
  getSecurityMetadata,
  isCalendarSpread,
  isDelta1Multileg,
  isSecurityQuantoFuture,
  isSpot,
  type FeeModeEnum,
  type OrderStrategy,
  type RequiredProperties,
  type Security,
} from '@talos/kyoko';

/**
 * Returns the DexName display value from Security.Metadata when it contains a DexName key.
 */
export function getSecurityDexName(metadata: Security['Metadata']): string | undefined {
  const dexName = getSecurityMetadata(metadata).DexName;
  if (typeof dexName !== 'string') {
    return undefined;
  }
  const trimmedDexName = dexName.trim();
  return trimmedDexName !== '' ? trimmedDexName : undefined;
}

/**
 * Asserts if the security must and can only be traded in terms of contracts.
 * @returns A function that asserts if the security must be traded in terms of contracts.
 */
export function assertSecurityOnlyPlaceInQtyContract(security?: Security) {
  return isCalendarSpread(security) || isSecurityQuantoFuture(security);
}

export const isOrderTypeLimitAllIn = (showAllInPrices: FeeModeEnum | null | undefined, security?: Security) => {
  return !!showAllInPrices;
};

export const isStrategySupportedBySecurity = (strategy: OrderStrategy, security: Security | undefined) => {
  if (!security) {
    return false;
  }
  if (strategy.InstrumentScope === 'All') {
    return true;
  }
  // For compatibility assume: Securities without a Composition are Native, strategys without an InstrumentScope are Native
  if (security.Composition !== 'Synthetic') {
    return strategy.InstrumentScope === undefined || strategy.InstrumentScope === 'Native';
  }
  return strategy.InstrumentScope === security.Composition;
};

export const areLegsNonSpot = (
  security: RequiredProperties<Security, 'MultilegDetails'>,
  securitiesBySymbol: Map<string, Security>
) => {
  const { leg1, leg2 } = getLegSecurities(security, securitiesBySymbol);
  return !isSpot(leg1) && !isSpot(leg2);
};

export const isDelta1NonSpotLegs = (
  security: Security | undefined,
  securitiesBySymbol: Map<string, Security> | undefined
) => Boolean(isDelta1Multileg(security) && securitiesBySymbol && areLegsNonSpot(security, securitiesBySymbol));

export const getLegSecurities = (security: Security, securitiesBySymbol: Map<string, Security>) => ({
  leg1: securitiesBySymbol.get(security?.MultilegDetails?.Legs?.[0]?.Symbol ?? ''),
  leg2: securitiesBySymbol.get(security?.MultilegDetails?.Legs?.[1]?.Symbol ?? ''),
});
